import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import {
  createAvailabilityException,
  updateAvailabilityException,
} from "../../api/Scheduling/availabilityExceptions";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { useServiceCategoriesQuery } from "../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import WindowCategoryPicker from "./WindowCategoryPicker";
import ShiftBreaksFields, { createBreakBlocks } from "./ShiftBreaksFields";
import { useBreakOverlapConfirmation } from "./BlockOverlapWarning";
import { shiftBreaksToBlockRanges } from "./time";
import { timeZoneLabel } from "./timeZones";
import { cycleWeekIndex, weekdayFromLocalDate, localTimeCandidates } from "./time";

function hasRepeatedTime(date, window, zone) {
  if (!date || !window.startTime || !window.endTime) return false;
  date = moment.utc(date).add(window.startDayOffset || 0, "days").format("YYYY-MM-DD");
  const endDate = window.endTime <= window.startTime ? moment.utc(date).add(1, "day").format("YYYY-MM-DD") : date;
  return localTimeCandidates(date, window.startTime, zone).length > 1 ||
    localTimeCandidates(endDate, window.endTime, zone).length > 1;
}

const replacementWindow = (categoryIds) => ({
  startTime: "09:00",
  endTime: "17:00",
  mode: "regular",
  serviceCategoryIds: categoryIds,
  breaks: [],
});

const ExceptionForm = ({ show, onHide, organizationRef, schedule, rules = [], exception, onSaved, initialDate, initialRuleId, occurrenceIssue }) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const { confirmOverlap, warning } = useBreakOverlapConfirmation();
  const categoryIds = schedule?.serviceCategoryIds || [];
  const isEdit = !!exception?.availabilityExceptionId;
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: "descendants", practitionerRef: schedule?.practitionerRef,
    organizationRef,
    active: true,
    enabled: !!organizationRef && show,
  });
  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category) => categoryIds.includes(category.serviceCategoryId))
        .map((category) => ({
          name: `${category.name} (${category.durationMinutes} min)`,
          value: category.serviceCategoryId,
          organizationRef: category.organizationRef, organizationName: category.organizationName || category.organizationRef,
        })),
    [categories, categoryIds]
  );

  const formik = useFormik({
    initialValues: {
      availabilityRuleId: "",
      localDate: initialDate || "",
      type: "closed",
      windows: [replacementWindow(categoryIds)],
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!values.availabilityRuleId) {
        failed("Pick the rule that covers this date.");
        return;
      }
      const body = {
        organizationRef,
        availabilityRuleId: values.availabilityRuleId,
        localDate: values.localDate,
        type: values.type,
      };
      const blockRanges = [];
      if (values.type === "replace") {
        if (!values.windows.length || values.windows.length > 8) {
          failed("Replace must send 1 to 8 windows.");
          return;
        }
        if (values.windows.some((window) => !window.serviceCategoryIds?.length)) {
          failed("Each replacement window needs at least one service category.");
          return;
        }
        for (const window of values.windows) {
          const breakResult = shiftBreaksToBlockRanges(window, values.localDate, schedule?.timezoneName || "UTC");
          if (breakResult.error) {
            failed(breakResult.error);
            return;
          }
          blockRanges.push(...breakResult.ranges);
        }
        body.windows = values.windows.map((window) => ({
          ...(window.startDayOffset ? { startDayOffset: window.startDayOffset } : {}),
          startTime: window.startTime,
          endTime: window.endTime,
          mode: window.mode || "regular",
          serviceCategoryIds: window.serviceCategoryIds,
          disambiguation: window.disambiguation || "reject",
        }));
      }
      setBtnLoading(true);
      let hoursSaved = false;
      try {
        if (isEdit) {
          await updateAvailabilityException({
            organizationRef,
            availabilityExceptionId: exception.availabilityExceptionId,
            type: values.type,
            windows: body.windows,
            expectedVersion: exception.version,
          });
        } else {
          await createAvailabilityException(body);
        }
        hoursSaved = true;
        await createBreakBlocks({
          organizationRef,
          scheduleId: schedule.scheduleId,
          ranges: blockRanges,
          confirmOverlap,
        });
        success(isEdit ? "Exception updated" : "Exception created");
        onSaved?.();
        onHide();
      } catch (error) {
        failed(
          hoursSaved && blockRanges.length
            ? `Hours saved, but a break could not be added. ${schedulingErrorMessage(error)}`
            : schedulingErrorMessage(error)
        );
        if (hoursSaved) {
          onSaved?.();
          onHide();
        }
      } finally {
        setBtnLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!show) return;
    if (exception) {
      formik.setValues({
        availabilityRuleId: exception.availabilityRuleId,
        localDate: exception.localDate,
        type: exception.type,
        windows: exception.windows?.length
          ? exception.windows.map((window) => ({ ...window, breaks: window.breaks || [] }))
          : [replacementWindow(categoryIds)],
      });
    } else {
      const covering = rules.find(rule => rule.availabilityRuleId === initialRuleId) || rules.find(
        (rule) =>
          rule.active &&
          (!initialDate || (rule.effectiveFrom <= initialDate && (!rule.effectiveTo || rule.effectiveTo >= initialDate)))
      );
      const originalWindows = occurrenceIssue && covering ? covering.weeklyWindows?.filter(window =>
        window.dayOfWeek === weekdayFromLocalDate(initialDate) && (window.weekIndex || 0) ===
          cycleWeekIndex(covering.cycleAnchorDate || covering.effectiveFrom, initialDate, covering.cycleWeeks || 1)) : [];
      formik.setValues({
        availabilityRuleId: covering?.availabilityRuleId || rules[0]?.availabilityRuleId || "",
        localDate: initialDate || "",
        type: occurrenceIssue ? "replace" : "closed",
        windows: originalWindows?.length ? originalWindows.map(window => ({ ...window, breaks: [] })) : [replacementWindow(categoryIds)],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, exception, initialDate, initialRuleId]);

  const coveringRules = rules.filter(
    (rule) =>
      rule.active &&
      (!formik.values.localDate ||
        (rule.effectiveFrom <= formik.values.localDate && (!rule.effectiveTo || rule.effectiveTo >= formik.values.localDate)))
  );

  return (
    <Modal show={show} onHide={() => { if (!btnLoading) onHide(); }} keyboard={!btnLoading} size="lg" centered backdrop="static" className="custom-dialog scheduling-dialog">
      <Modal.Header closeButton={!btnLoading} className="border-0">
        <Modal.Title>{isEdit ? "Edit date change" : "Change hours for a date"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="schedule-zone-note">Schedule time: {timeZoneLabel(schedule?.timezoneName)}</p>
        {occurrenceIssue && <p role="alert">{occurrenceIssue.localTime.replace("T", " ")} {occurrenceIssue.reason === "ambiguous" ? "occurs twice" : "does not exist"}.
          Choose valid hours or a day off for {initialDate}. This changes only this date.</p>}
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <fieldset disabled={btnLoading}>
          <Row>
            <Col>
              <label>Date</label>
              <input
                type="date"
                className="form-control"
                value={formik.values.localDate}
                disabled={isEdit || !!occurrenceIssue}
                onChange={(event) => formik.setFieldValue("localDate", event.target.value)}
              />
            </Col>
            <Col>
              <label>Work schedule for this date</label>
              <select
                className="form-control"
                value={formik.values.availabilityRuleId}
                disabled={isEdit || !!occurrenceIssue}
                onChange={(event) => formik.setFieldValue("availabilityRuleId", event.target.value)}
              >
                <option value="">Select work schedule</option>
                {coveringRules.map((rule) => (
                  <option key={rule.availabilityRuleId} value={rule.availabilityRuleId}>
                    {rule.effectiveFrom} – {rule.effectiveTo || "No end date"}
                  </option>
                ))}
              </select>
            </Col>
            <Col>
              <label>Change to make</label>
              <select
                className="form-control"
                value={formik.values.type}
                onChange={(event) => formik.setFieldValue("type", event.target.value)}
              >
                <option value="closed">Day off · no appointments</option>
                <option value="replace">Different working hours</option>
              </select>
            </Col>
          </Row>
          {formik.values.type === "replace" ? (
            <div className="scheduling-window-list">
              {formik.values.windows.map((window, index) => (
                <div key={index} className="scheduling-window-row">
                  {formik.values.windows.length > 1 ? (
                    <div className="scheduling-window-row-header">
                      <Button
                        variant="secondary"
                        onClick={() =>
                          formik.setFieldValue(
                            "windows",
                            formik.values.windows.filter((_, idx) => idx !== index)
                          )
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  ) : null}
                  <div className="scheduling-window-fields">
                    <Row className="align-items-end g-2">
                      <Col md>
                        <label>Start</label>
                        <input
                          type="time"
                          className="form-control"
                          value={window.startTime}
                          onChange={(event) => {
                            const next = formik.values.windows.slice();
                            next[index] = { ...next[index], startTime: event.target.value, disambiguation: "reject" };
                            formik.setFieldValue("windows", next);
                          }}
                        />
                      </Col>
                    <Col md>
                      <label>End</label>
                      <input
                        type="time"
                        className="form-control"
                        value={window.endTime}
                        onChange={(event) => {
                          const next = formik.values.windows.slice();
                          next[index] = { ...next[index], endTime: event.target.value, disambiguation: "reject" };
                          formik.setFieldValue("windows", next);
                        }}
                      />
                    </Col>
                  </Row>
                  </div>
                  {!!window.startDayOffset && <p className="schedule-help">These hours start the following day.</p>}
                  <div className="scheduling-window-categories-block">
                    <WindowCategoryPicker
                      options={categoryOptions}
                      value={window.serviceCategoryIds || []}
                      onChange={(nextIds) => {
                        const next = formik.values.windows.slice();
                        next[index] = { ...next[index], serviceCategoryIds: nextIds };
                        formik.setFieldValue("windows", next);
                      }}
                    />
                  </div>
                  {hasRepeatedTime(formik.values.localDate, window, schedule?.timezoneName || "UTC") && <div>
                    <label htmlFor={`occurrence-${index}`}>Repeated hour</label>
                    <select id={`occurrence-${index}`} className="form-control" value={window.disambiguation || "reject"}
                      onChange={event => formik.setFieldValue(`windows.${index}.disambiguation`, event.target.value)}>
                      <option value="reject">Choose an occurrence</option>
                      <option value="earlier">First occurrence (before clocks go back)</option>
                      <option value="later">Second occurrence (after clocks go back)</option>
                    </select>
                  </div>}
                  <ShiftBreaksFields
                    shift={window} localDate={formik.values.localDate} timezoneName={schedule?.timezoneName || "UTC"}
                    breaks={window.breaks || []}
                    onChange={(breaks) => {
                      const next = formik.values.windows.slice();
                      next[index] = { ...next[index], breaks };
                      formik.setFieldValue("windows", next);
                    }}
                    removeAriaLabel={(pauseIndex) => `Remove break ${pauseIndex + 1} from replacement window ${index + 1}`}
                  />
                </div>
              ))}
            </div>
          ) : null}
          {formik.values.type === "replace" ? (
            <div className="scheduling-window-add">
              <Button
                variant="secondary"
                disabled={formik.values.windows.length >= 8}
                onClick={() =>
                  formik.setFieldValue("windows", [
                    ...formik.values.windows,
                    replacementWindow(categoryIds),
                  ])
                }
              >
                Add replacement window
              </Button>
            </div>
          ) : null}
          </fieldset>
          {warning}
          {!warning && <div className="btn-wrap" style={{ display: "flex", marginTop: "1rem" }}>
            <Button type="button" variant="secondary" disabled={btnLoading} onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>}
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ExceptionForm;
